import {
	BoolEnum,
	IdentityCode,
	OnboardMerchantDto,
	PaymentSetupDto,
	PaymentSetupStatus,
	TierOneDocumentCode,
	TierThreeDocumentCode,
	TierTwoDocumentCode,
} from "../../../../../models/payment-setup.dto";

/**
 * Rows loaded from a saved setup keep their record `id`, and send it back on
 * update so the backend changes that record instead of adding a new one.
 */
type SavedRecordId = number | string;

export type DirectorFormValues = {
	id?: SavedRecordId;
	lastName: string;
	otherNames: string;
	email: string;
	phoneNumber: string;
	identityCode: IdentityCode | "";
	identity: string;
	verificationId: string;
};

export type ContactFormValues = {
	id?: SavedRecordId;
	name: string;
	email: string;
	phone: string;
};

export type ProductFormValues = {
	id?: SavedRecordId;
	code: string;
	name: string;
	description: string;
	destinationDetails: {
		destinationId: string;
		destinationTypeId: string;
		destinationBranchName: string;
		destinationAccountNumber: string;
		destinationAccountName: string;
		receiveStatement: BoolEnum;
	};
};

export type PaymentSetupFormValues = {
	// Business
	companyName: string;
	tradeName: string;
	alias: string;
	tier: string;
	country: string;
	typeOfCompany: string;
	companyCategories: string;
	countryMerchantCategoryId: string;
	companyRegistrationNumber: string;
	vatRegistrationNumber: string;
	taxIdentificationNumber: string;
	dateOfIncorporation: string;
	dateOfCommencement: string;
	companyLogo: string;
	// Primary contact
	surname: string;
	otherNames: string;
	email: string;
	phoneNumber: string;
	// Address & departmental contacts
	officeAddress: {
		officeAddress: string;
		officeOwnership: string;
		officeCity: string;
		officeRegion: string;
		officeDistrict: string;
		officePhone: string;
		officeMobile: string;
		officeGpsAddress: string;
	};
	contacts: ContactFormValues[];
	companyDirectors: DirectorFormValues[];
	/** Uploaded file URL keyed by document code */
	documents: Record<string, string>;
	/** Saved document record ids keyed by document code */
	documentIds: Record<string, SavedRecordId>;
	products: ProductFormValues[];
};

export const emptyDirector: DirectorFormValues = {
	lastName: "",
	otherNames: "",
	email: "",
	phoneNumber: "",
	identityCode: "",
	identity: "",
	verificationId: "",
};

export const emptyContact: ContactFormValues = { name: "", email: "", phone: "" };

export const emptyProduct: ProductFormValues = {
	code: "",
	name: "",
	description: "",
	destinationDetails: {
		destinationId: "",
		destinationTypeId: "",
		destinationBranchName: "",
		destinationAccountNumber: "",
		destinationAccountName: "",
		receiveStatement: BoolEnum.N,
	},
};

export const getDefaultFormValues = (
	country: string,
	companyName = "",
): PaymentSetupFormValues => ({
	companyName,
	tradeName: "",
	alias: "",
	tier: "",
	country,
	typeOfCompany: "",
	companyCategories: "",
	countryMerchantCategoryId: "",
	companyRegistrationNumber: "",
	vatRegistrationNumber: "",
	taxIdentificationNumber: "",
	dateOfIncorporation: "",
	dateOfCommencement: "",
	companyLogo: "",
	surname: "",
	otherNames: "",
	email: "",
	phoneNumber: "",
	officeAddress: {
		officeAddress: "",
		officeOwnership: "",
		officeCity: "",
		officeRegion: "",
		officeDistrict: "",
		officePhone: "",
		officeMobile: "",
		officeGpsAddress: "",
	},
	contacts: [],
	companyDirectors: [{ ...emptyDirector }],
	documents: {},
	documentIds: {},
	products: [],
});

const toDateInput = (value?: Date | string | null) =>
	value ? new Date(value).toISOString().slice(0, 10) : "";

/** Maps a saved payment setup (draft or submitted) back into form values. */
export const paymentSetupToFormValues = (
	setup: PaymentSetupDto,
	fallbackCountry: string,
): PaymentSetupFormValues => {
	const defaults = getDefaultFormValues(fallbackCountry);
	const address = setup.officeAddress?.[0];

	return {
		...defaults,
		companyName: setup.companyName || "",
		tradeName: setup.tradeName || "",
		alias: setup.alias || "",
		tier: setup.tier || "",
		country: setup.country || fallbackCountry,
		typeOfCompany: setup.typeOfCompany || "",
		companyCategories: setup.companyCategories || "",
		countryMerchantCategoryId: setup.countryMerchantCategoryId || "",
		companyRegistrationNumber: setup.companyRegistrationNumber || "",
		vatRegistrationNumber: setup.vatRegistrationNumber || "",
		taxIdentificationNumber: setup.taxIdentificationNumber || "",
		dateOfIncorporation: toDateInput(setup.dateOfIncorporation),
		dateOfCommencement: toDateInput(setup.dateOfCommencement),
		companyLogo: setup.companyLogo || "",
		surname: setup.surname || "",
		otherNames: setup.otherNames || "",
		email: setup.email || "",
		phoneNumber: setup.phoneNumber || "",
		officeAddress: address
			? {
					officeAddress: address.officeAddress || "",
					officeOwnership: address.officeOwnership || "",
					officeCity: address.officeCity || "",
					officeRegion: address.officeRegion || "",
					officeDistrict: address.officeDistrict || "",
					officePhone: address.officePhone || "",
					officeMobile: address.officeMobile || "",
					officeGpsAddress: address.officeGpsAddress || "",
			  }
			: defaults.officeAddress,
		contacts: (setup.contacts || []).map((contact) => ({
			id: contact.id,
			name: contact.name || "",
			email: contact.email || "",
			phone: contact.phone || "",
		})),
		companyDirectors: setup.companyDirectors?.length
			? setup.companyDirectors.map((director) => ({
					id: director.id,
					lastName: director.lastName || "",
					otherNames: director.otherNames || "",
					email: director.email || "",
					phoneNumber: director.phoneNumber || "",
					identityCode: (director.identityCode as IdentityCode) || "",
					identity: director.identity || "",
					verificationId: director.verificationId || "",
			  }))
			: defaults.companyDirectors,
		documents: (setup.documents || []).reduce<Record<string, string>>(
			(documents, document) => {
				if (document.code && document.fileUrl) {
					documents[document.code] = document.fileUrl;
				}
				return documents;
			},
			{},
		),
		documentIds: (setup.documents || []).reduce<Record<string, SavedRecordId>>(
			(ids, document) => {
				if (document.code && document.id != null) ids[document.code] = document.id;
				return ids;
			},
			{},
		),
		products: (setup.products || []).map((product) => ({
			id: product.id,
			code: product.code || "",
			name: product.name || "",
			description: product.description || "",
			destinationDetails: {
				destinationId: product.paymentInstitutionId || "",
				destinationTypeId: product.paymentInstitutionTypeId || "",
				destinationBranchName: product.paymentInstitutionBranchName || "",
				destinationAccountNumber: product.paymentInstitutionAccountNumber || "",
				destinationAccountName: product.paymentInstitutionAccountName || "",
				receiveStatement: product.receiveStatement ? BoolEnum.Y : BoolEnum.N,
			},
		})),
	};
};

const ACCEPTED_DOCUMENT_CODES: string[] = Object.values(TierThreeDocumentCode);

const blankToUndefined = (value?: string) =>
	value && value.trim() ? value.trim() : undefined;

const isFilled = (...values: (string | undefined)[]) =>
	values.every((value) => !!value?.trim());

/**
 * Which sub-records the backend accepts an `id` on, to update the saved record
 * instead of adding a new one. TODO: turn each on once the backend confirms it;
 * until then it rejects them ("companyDirectors[0].id" is not allowed, etc.).
 * The office address is never sent with an id.
 */
const SEND_SAVED_RECORD_IDS = {
	documents: false,
	directors: false,
	contacts: false,
	products: false,
};

/** Adds the saved record's id, when there is one and the backend accepts it */
const withId = <T extends object>(
	enabled: boolean,
	id: SavedRecordId | undefined,
	item: T,
) => (enabled && id != null && id !== "" ? { id, ...item } : item);

const sameText = (a?: string | null, b?: string | null) =>
	(a || "").trim().toLowerCase() === (b || "").trim().toLowerCase();

/**
 * Copies the ids the backend assigned to sub-records (after a draft save) onto
 * the matching form rows, so later saves update those records. Rows are matched
 * by their content, since drafts leave out incomplete rows and positions shift.
 */
export const getSavedRecordIds = (
	values: PaymentSetupFormValues,
	saved: PaymentSetupDto,
) => {
	const updates: { path: string; id: SavedRecordId }[] = [];

	values.companyDirectors.forEach((director, index) => {
		if (director.id != null) return;
		const match = saved.companyDirectors?.find(
			(item) =>
				sameText(item.lastName, director.lastName) &&
				sameText(item.otherNames, director.otherNames),
		);
		if (match?.id != null) updates.push({ path: `companyDirectors.${index}.id`, id: match.id });
	});

	values.contacts.forEach((contact, index) => {
		if (contact.id != null) return;
		const match = saved.contacts?.find(
			(item) => sameText(item.name, contact.name) && sameText(item.email, contact.email),
		);
		if (match?.id != null) updates.push({ path: `contacts.${index}.id`, id: match.id });
	});

	values.products.forEach((product, index) => {
		if (product.id != null) return;
		const match = saved.products?.find((item) => sameText(item.code, product.code));
		if (match?.id != null) updates.push({ path: `products.${index}.id`, id: match.id });
	});

	(saved.documents || []).forEach((document) => {
		if (document.code && document.id != null && values.documentIds[document.code] == null) {
			updates.push({ path: `documentIds.${document.code}`, id: document.id });
		}
	});

	return updates;
};

/**
 * Builds the API payload from form values, dropping blank optional values so
 * the backend doesn't reject empty strings.
 *
 * Drafts can be saved part-way through the form, so with `draft` every blank
 * value is dropped and incomplete directors, contacts and products are left
 * out until they have the fields the backend requires. On final submit the
 * form's own validation has already made sure those fields are filled.
 */
export const formValuesToPayload = (
	values: PaymentSetupFormValues,
	{ draft = false }: { draft?: boolean } = {},
): OnboardMerchantDto => {
	// Required fields are sent as-is on submit, but may still be blank in a draft
	const required = (value: string) => (draft ? blankToUndefined(value) : value.trim()) as string;
	const nonEmpty = <T,>(items: T[]) => (items.length ? items : undefined);

	const address = values.officeAddress;
	const hasAddress = !!blankToUndefined(address.officeAddress);

	const contacts = values.contacts.filter(
		(contact) => !draft || isFilled(contact.name, contact.email, contact.phone),
	);
	const directors = values.companyDirectors.filter(
		(director) => !draft || isFilled(director.lastName, director.otherNames),
	);
	const products = values.products
		.filter(
			(product) => !draft || isFilled(product.code, product.name, product.description),
		)
		.map((product) => {
			const details = product.destinationDetails;
			const detailsComplete = isFilled(
				details.destinationId,
				details.destinationTypeId,
				details.destinationBranchName,
				details.destinationAccountNumber,
				details.destinationAccountName,
			);
			const { id, ...productFields } = product;
			const item = withId(SEND_SAVED_RECORD_IDS.products, id, productFields);
			// The provider requires all settlement fields together, or none
			return detailsComplete || !draft
				? item
				: { ...item, destinationDetails: undefined as any };
		});
	const documents = Object.entries(values.documents)
		// Only codes the backend accepts (the full tier 3 set covers every tier)
		.filter(([code, file]) => !!file && ACCEPTED_DOCUMENT_CODES.includes(code))
		.map(([code, file]) => withId(SEND_SAVED_RECORD_IDS.documents, values.documentIds[code], { code, file }));

	return {
		companyName: required(values.companyName),
		tradeName: blankToUndefined(values.tradeName),
		alias: blankToUndefined(values.alias),
		tier: required(values.tier),
		country: required(values.country),
		typeOfCompany: blankToUndefined(values.typeOfCompany),
		companyCategories: required(values.companyCategories),
		countryMerchantCategoryId: blankToUndefined(
			values.countryMerchantCategoryId,
		),
		companyRegistrationNumber: blankToUndefined(values.companyRegistrationNumber),
		vatRegistrationNumber: blankToUndefined(values.vatRegistrationNumber),
		taxIdentificationNumber: blankToUndefined(values.taxIdentificationNumber),
		dateOfIncorporation: values.dateOfIncorporation
			? new Date(values.dateOfIncorporation)
			: undefined,
		dateOfCommencement: values.dateOfCommencement
			? new Date(values.dateOfCommencement)
			: undefined,
		companyLogo: required(values.companyLogo),
		surname: required(values.surname),
		otherNames: required(values.otherNames),
		email: blankToUndefined(values.email),
		phoneNumber: blankToUndefined(values.phoneNumber),
		officeAddress: hasAddress
			? [
					// The backend rejects an id on the office address ("officeAddress[0].id"
					// is not allowed); it's a single entry, so it's replaced as a whole
					{
						officeAddress: address.officeAddress.trim(),
						officeOwnership: blankToUndefined(address.officeOwnership),
						officeCity: blankToUndefined(address.officeCity),
						officeRegion: blankToUndefined(address.officeRegion),
						officeDistrict: blankToUndefined(address.officeDistrict),
						officePhone: blankToUndefined(address.officePhone),
						officeMobile: blankToUndefined(address.officeMobile),
						officeGpsAddress: blankToUndefined(address.officeGpsAddress),
					},
			  ]
			: undefined,
		contacts: nonEmpty(
			contacts.map((contact) =>
				withId(SEND_SAVED_RECORD_IDS.contacts, contact.id, {
					name: contact.name,
					email: contact.email.trim(),
					phone: contact.phone,
				}),
			),
		),
		companyDirectors: (draft ? nonEmpty : (items: any[]) => items)(
			directors.map((director) => withId(SEND_SAVED_RECORD_IDS.directors, director.id, {
				lastName: director.lastName.trim(),
				otherNames: director.otherNames.trim(),
				email: blankToUndefined(director.email),
				phoneNumber: blankToUndefined(director.phoneNumber),
				identityCode: director.identityCode || undefined,
				identity: blankToUndefined(director.identity),
				verificationId: blankToUndefined(director.verificationId),
			})),
		) as OnboardMerchantDto["companyDirectors"],
		documents: (draft ? nonEmpty(documents) : documents) as OnboardMerchantDto["documents"],
		products: nonEmpty(products),
	};
};

/* ------------------------------ Reference data ----------------------------- */

/**
 * Payment API responses may arrive as the raw list, `{ data: [] }`, the
 * provider's `{ data: { data: [] } }` envelope, or that envelope wrapped again
 * (`{ data: { data: { data: [] } } }`). Normalise them to an array.
 */
export const toList = <T = any>(response: any): T[] => {
	if (Array.isArray(response)) return response;
	if (Array.isArray(response?.data)) return response.data;
	if (Array.isArray(response?.data?.data)) return response.data.data;
	if (Array.isArray(response?.data?.data?.data)) return response.data.data.data;
	return [];
};

/** Unwraps a single record from the same set of possible envelopes. */
export const toRecord = <T = any>(response: any): T | undefined => {
	if (!response) return undefined;
	if (response.data?.data && !Array.isArray(response.data.data)) {
		return response.data.data;
	}
	if (response.data && !Array.isArray(response.data)) return response.data;
	return response;
};

export type PaymentTier = {
	name: string;
	description?: string;
	minAmount?: string | number;
	maxAmount?: string | number;
};

/** e.g. "1.00 – 5,000.00" from the tier's transaction limits */
export const getTierRange = (tier: PaymentTier) => {
	const format = (amount?: string | number) =>
		amount !== undefined && amount !== null && amount !== ""
			? Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2 })
			: "";
	const min = format(tier.minAmount);
	const max = format(tier.maxAmount);
	if (min && max) return `${min} – ${max}`;
	if (min) return `from ${min}`;
	if (max) return `up to ${max}`;
	return "";
};

export type MerchantCategory = {
	id: string;
	name: string;
	label?: string;
};

/** companyCategories is stored as a comma-separated list of merchant category names */
export const splitCategories = (value?: string) =>
	(value || "")
		.split(",")
		.map((name) => name.trim())
		.filter(Boolean);

export type PaymentDestination = {
	destinationId: string;
	destinationTypeId: string;
	name: string;
	code: string;
	branches?: (string | { name?: string; branchName?: string })[];
};

export const getBranchNames = (destination?: PaymentDestination) =>
	(destination?.branches || [])
		.map((branch) =>
			typeof branch === "string" ? branch : branch.name || branch.branchName || "",
		)
		.filter(Boolean);

/* -------------------------------- Documents -------------------------------- */

const DOCUMENT_LABELS: Record<string, string> = {
	ghana_card: "Ghana Card",
	operation_license: "Operating licence",
	product_service_description: "Product / service description",
	tin_number: "TIN certificate",
	business_registration: "Business registration certificate",
	form_3a: "Form 3A",
	directors_identification: "Directors' identification",
	ownership_structure: "Ownership structure",
	regulator_license: "Regulator licence",
	product_description: "Product description",
	aml_fraud_policy: "AML / fraud policy",
	data_protection_certificate: "Data protection certificate",
	vulnerability_test_report: "Vulnerability test report",
	due_diligence: "Due diligence",
	bank_statements: "Bank statements",
	audited_financial_statements: "Audited financial statements",
	shareholding_structure_additional: "Additional shareholding structure",
	source_of_funds_explanation: "Source of funds explanation",
};

export const getDocumentLabel = (code: string) =>
	DOCUMENT_LABELS[code] ||
	code.replace(/_/g, " ").replace(/^\w/, (char) => char.toUpperCase());

/** Document codes required for a tier name such as "tier 2". */
export const getRequiredDocumentCodes = (tier: string): string[] => {
	const tierNumber = Number(tier.match(/\d+/)?.[0]);

	switch (tierNumber) {
		case 1:
			return Object.values(TierOneDocumentCode);
		case 2:
			return Object.values(TierTwoDocumentCode);
		case 3:
			return Object.values(TierThreeDocumentCode);
		default:
			return [];
	}
};

/* --------------------------------- Status ---------------------------------- */

export const STATUS_DETAILS: Record<
	PaymentSetupStatus,
	{ label: string; className: string; description: string }
> = {
	[PaymentSetupStatus.DRAFT]: {
		label: "Draft",
		className: "bg-gray-100 text-gray-700",
		description: "This request hasn't been submitted yet.",
	},
	[PaymentSetupStatus.PENDING]: {
		label: "Under review",
		className: "bg-amber-50 text-amber-700",
		description:
			"The provider is reviewing this request. If they need corrections, they'll appear below.",
	},
	[PaymentSetupStatus.SUCCESSFUL]: {
		label: "Live",
		className: "bg-green-50 text-green-700",
		description: "Payments are set up and the company can transact.",
	},
	[PaymentSetupStatus.FAILED]: {
		label: "Failed",
		className: "bg-red-50 text-red-600",
		description:
			"Setup didn't complete. Review any issues below, correct the request and resubmit.",
	},
	[PaymentSetupStatus.REJECTED]: {
		label: "Rejected",
		className: "bg-red-50 text-red-600",
		description:
			"The provider declined this request and it is closed. Start a new request if the company's details have changed.",
	},
};

/* ---------------------------------- Forms ---------------------------------- */

export const inputClassName = (hasError?: boolean) =>
	`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
		hasError
			? "border border-red-500 focus:border-red-500 focus:outline-red-500"
			: "border-none"
	}`;

export const GHANA_CARD_PATTERN = /^GHA-\d{9}-\d$/;
export const GHANA_PHONE_PATTERN = /^233\d{9}$/;
export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
